<cdk-virtual-scroll-viewport #viewport>
    @if (gridReady) {
        <table
            cdk-table
            sklDisableVirtualScroll
            [dataSource]="virtualRowsData$"
            sklGridResizable
            [sklGridResizableData]="columnSizes$.value"
            (sklGridResizableOnResized)="handleColumnResized($event)"
            cdkDropList
            [cdkDropListData]="displayedColumns$.value"
            [cdkDropListSortPredicate]="sortPredicate.bind(this)"
            (cdkDropListDropped)="handleDragAndDrop($event)"
            cdkDropListOrientation="horizontal"
            cdkDropListLockAxis="x"
            class="skl-table"
            [style.paddingBottom.px]="scrollBottomOffset"
            [class.skl-table--group]="isGroupMode"
            [class.skl-table--no-headers]="!displayHeaders">

            <!-- Cell -->
            @for (column of columns$ | async; track column; let idx = $index) {
                <ng-container
                    [cdkColumnDef]="getDef(column)">
                    <th
                        id="header-{{ column.columnDef | lowercase }}-{{ idx + 1 }}"
                        [attr.data-testid]="'header-' + column.columnDef | lowercase"
                        [class]="(column?.styleClass! | sklMatch:'column') + ' table-header cdk-header-cell'"
                        cdk-header-cell
                        *cdkHeaderCellDef
                        cdkDrag
                        [cdkDragData]="column.columnDef"
                        [cdkDragDisabled]="isDragDisabled(column.isMovable)"
                        [tooltip]="column?.headerTooltip || column?.header || column?.label!"
                        [tooltipContainerRef]="headerTemplate"
                        (click)="handleHeaderClick($event, column)">
                        <div class="header-wrapper" [class.header-wrapper--hidden]="!isHeaderVisible">
                            <div class="header-content">
                                <span
                                    class="drag-handle"
                                    cdkDragHandle
                                    [cdkDragHandleDisabled]="isDragDisabled(column.isMovable)">
                                </span>
                                <div class="header-template" #headerTemplate>
                                    <ng-container
                                        [ngTemplateOutlet]="getTemplate(column?.headerTemplate ?? 'headerTemplate')"
                                        [ngTemplateOutletContext]="{col:column, ctx:'header'}">
                                    </ng-container>
                                </div>
                                <skl-header-resizer
                                    [resizable]="column.isResizable ?? true"
                                    [columnDef]="column.columnDef">
                                </skl-header-resizer>
                                @if (isSortable(column.sortingBy!)) {
                                    <skl-sort-icon
                                        [sortDirection]="options.sorting?.direction!">
                                    </skl-sort-icon>
                                }
                            </div>
                        </div>
                    </th>

                    <td
                        id="cell-{{ column.columnDef | lowercase }}-{{ idx + 1 }}"
                        [attr.data-testid]="'cell-' + column.columnDef | lowercase"
                        cdk-cell
                        *cdkCellDef="let entity"
                        sklGridCellResize
                        [class]="(column?.styleClass! | sklMatch:'column') + ' cdk-cell'"
                        [tooltip]="(column?.cellTooltip && column?.template !== 'buttonTemplate' ? column?.cellTooltip(entity) : null)!"
                        [attr.data-label]="column?.label ?? column.header">
                        <div class="cell-wrapper">
                            <div class="cell-content">
                                <ng-container
                                    [ngTemplateOutlet]="getTemplate(column?.template ?? 'labelTemplate')"
                                    [ngTemplateOutletContext]="{row:entity, col:column, ctx:'cell'}">
                                </ng-container>
                            </div>
                        </div>
                    </td>

                    <td
                        id="cell-summary-{{ column.columnDef | lowercase }}-{{ idx + 1 }}"
                        [attr.data-testid]="'cell-summary-' + column.columnDef | lowercase"
                        cdk-footer-cell
                        *cdkFooterCellDef="let entity"
                        sklGridCellResize
                        [class]="['cell-summary', column?.styleClass! | sklMatch:'column', !isFooterVisible || noDataCheck ? 'cell-summary--hidden' : '', 'cdk-footer-cell'].join(' ')"
                        [attr.data-label]="column?.label ?? column.header">
                        @if (!noDataCheck && isFooterVisible) {
                            <div class="cell-wrapper">
                                <div class="cell-content" >
                                    @if (!column?.summary) {
                                        <span>&nbsp;</span>
                                    }
                                    @if (column?.summary && column.summary !== null) {
                                        <span>{{column?.summary()}}</span>
                                    }
                                </div>
                            </div>
                        }
                    </td>
                </ng-container>
            }

            <tr
                cdk-header-row
                [class.row-sticky-top]="options.stickyHeader !== false"
                [style.top]="stickyHeaderTop"
                *cdkHeaderRowDef="columnMapper"
                (contextmenu)="handleHeaderContextMenu($event)">
            </tr>

            <!-- Row config -->
            <ng-template
                let-entity
                let-idx="index"
                cdkRowDef
                [cdkRowDefColumns]="columnMapper"
                cdkVirtualFor
                [cdkVirtualForOf]="dataSource$">
                <tr
                    cdk-row
                    id="row-{{ idx + 1 }}"
                    tabindex="-1"
                    [class.row]="isSelectionTable"
                    [class.active]="isSelectionTable ? isRowActive(idx) : false"
                    [class.selected]="isSelectionTable ? selection.isSelected(entity) : false"
                    [class.hidden]="isGroupEntityHidden(entity)"
                    [class.italic]="isRowItalicStyled(entity)"
                    (click)="handleRowSingleClick($event, entity)"
                    (dblclick)="handleRowDblClick($event, entity)"
                    (auxclick)="handleRowMiddleClick($event, entity)"
                    (contextmenu)="handleRowContextMenu($event, entity)">
                </tr>
            </ng-template>

            <ng-container cdkColumnDef="groupHeader">
                <td
                    colspan="999"
                    cdk-cell
                    (click)="toggleGroup(group)"
                    *cdkCellDef="let group">
                    <div class="cell-group-header">
                        <skl-icon
                            class="cell-group-header__button"
                            [class.cell-group-header__button--up]="group.expanded"
                            [class.cell-group-header__button--down]="!group.expanded"
                            name="angle-up">
                        </skl-icon>
                        <span class="cell-group-header__label">
                            {{ group.label }}
                        </span>
                    </div>
                </td>
            </ng-container>
            <tr class="row" cdk-row *cdkRowDef="let entity; columns: ['groupHeader']; when: isGroupItem"></tr>

            <ng-container>
                <tr
                    cdk-footer-row
                    class="row-sticky-bottom"
                    [style.bottom]="stickyFooterBottom"
                    *cdkFooterRowDef="columnMapper">
                </tr>
            </ng-container>
        </table>
    }
    <!-- No Data message -->
    @if (!options.hideNoDataRow && noDataCheck) {
        <div class="skl-table-no-data" [style]="{'margin-top': noDataRowHeight}">
            @if (!noDataRowTemplate) {
                {{ 'ui.grid.message.lack-of-results' | translate }}
            } @else {
                <ng-container *ngTemplateOutlet="noDataRowTemplate"></ng-container>
            }
        </div>
    }
</cdk-virtual-scroll-viewport>


<!-- Templates -->
<ng-template [sklGridTemplate]="'multiSelectTemplate'" let-row="row" let-col="col" let-ctx="ctx">
    @if (ctx === 'header') {
        <ins-checkbox
            [indeterminate]="selection.isIndeterminate()"
            (click)="headerSelection($event)"
            [ngModel]="selection.isAllSelected()">
        </ins-checkbox>
    } @else if (ctx === 'cell') {
        <ins-checkbox
            (click)="rowSelection($event, row)"
            [ngModel]="selection.isSelected(row)">
        </ins-checkbox>
    }
</ng-template>

<ng-template [sklGridTemplate]="'headerTemplate'" let-col="col">
    <span>{{ col.header }}</span>
</ng-template>

<ng-template [sklGridTemplate]="'starTemplate'" let-row="row" let-col="col" let-ctx="ctx">
    <skl-star [value]="row[col.columnDef]" (click)="$event.stopPropagation(); col.action($event, row)"></skl-star>
</ng-template>

<ng-template [sklGridTemplate]="'buttonTemplate'" let-row="row" let-col="col" let-ctx="ctx" let-entity="entity">
    @if (col.isButtonVisible?.(row) ?? true) {
        <skl-button typeName="transparent" [tooltip]="col.cellTooltip?.(entity)" (click)="col.action($event, row, dataSource$)"><skl-icon [name]="col.icon"></skl-icon></skl-button>
    }
</ng-template>

<ng-template [sklGridTemplate]="'labelTemplate'" let-row="row" let-col="col" let-ctx="ctx">
    <span>{{ col.cell(row) }}</span>
</ng-template>

<ng-template [sklGridTemplate]="'sklHrefLinkTemplate'" let-row="row" let-col="col" let-ctx="ctx">
    <a class="link link--dotted" [routerLink]="col.getRouteObject(row)?.url | sklCompanyLink" [queryParams]="col.getRouteObject(row)?.params" [target]="col.target || '_self'">{{ col.cell(row) }}</a>
</ng-template>

<ng-template [sklGridTemplate]="'linkActionTemplate'" let-row="row" let-col="col" let-ctx="ctx">
    <span class="text--ellipsis link link--dotted" (click)="col.action(row)">{{ col.cell(row) }}</span>
</ng-template>